"use client";

import type { FormatGroup, NormBlock, NormBox, SpreadAnchor } from "../templates/types";

const FORMAT_PAGE_MM: Record<FormatGroup, { w: number; h: number }> = {
  a4_portrait: { w: 210, h: 297 },
  a4_landscape: { w: 297, h: 210 },
  a5_portrait: { w: 148, h: 210 },
  a5_landscape: { w: 210, h: 148 },
};

function applyAnchor(target: { x: number; y: number; w: number; h: number }, anchor?: SpreadAnchor) {
  if (!anchor || anchor === "both") return target;
  const half = target.w / 2;
  if (anchor === "left") return { ...target, w: half };
  return { ...target, x: target.x + half, w: half };
}

function mapBox(box: NormBox, target: { x: number; y: number; w: number; h: number }) {
  return {
    x: target.x + box.x * target.w,
    y: target.y + box.y * target.h,
    w: box.w * target.w,
    h: box.h * target.h,
  };
}

function shapePoints(shapeId: string, x: number, y: number, w: number, h: number) {
  const px = (v: number) => x + (v / 100) * w;
  const py = (v: number) => y + (v / 100) * h;
  const polygons: Record<string, Array<[number, number]>> = {
    triangle: [[50, 0], [100, 100], [0, 100]],
    rhombus: [[50, 0], [100, 50], [50, 100], [0, 50]],
    pentagon: [[50, 0], [100, 38], [81, 100], [19, 100], [0, 38]],
    octagon: [[30, 0], [70, 0], [100, 30], [100, 70], [70, 100], [30, 100], [0, 70], [0, 30]],
    star: [[50, 0], [61, 35], [98, 35], [68, 57], [79, 91], [50, 70], [21, 91], [32, 57], [2, 35], [39, 35]],
    kite: [[50, 0], [86, 40], [50, 100], [14, 40]],
  };
  return (polygons[shapeId] || polygons.rhombus).map(([a, b]) => `${px(a)},${py(b)}`).join(" ");
}

function ImageBlockPreview({
  x,
  y,
  w,
  h,
  maskShapeId,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  maskShapeId?: string;
}) {
  const rx = Math.min(8, Math.max(2, Math.min(w, h) * 0.08));
  const cx = x + w * 0.3;
  const cy = y + h * 0.32;
  const r = Math.max(1.6, Math.min(w, h) * 0.08);
  const hill = `${x + w * 0.12},${y + h * 0.75} ${x + w * 0.34},${y + h * 0.52} ${x + w * 0.5},${y + h * 0.66} ${x + w * 0.66},${y + h * 0.48} ${x + w * 0.9},${y + h * 0.78}`;

  if (maskShapeId === "heart") {
    const path = `
      M ${x + w * 0.5} ${y + h * 0.9}
      C ${x + w * 0.18} ${y + h * 0.7}, ${x + w * 0.08} ${y + h * 0.54}, ${x + w * 0.08} ${y + h * 0.34}
      C ${x + w * 0.08} ${y + h * 0.16}, ${x + w * 0.24} ${y + h * 0.08}, ${x + w * 0.38} ${y + h * 0.12}
      C ${x + w * 0.46} ${y + h * 0.14}, ${x + w * 0.5} ${y + h * 0.22}, ${x + w * 0.5} ${y + h * 0.28}
      C ${x + w * 0.5} ${y + h * 0.22}, ${x + w * 0.54} ${y + h * 0.14}, ${x + w * 0.62} ${y + h * 0.12}
      C ${x + w * 0.76} ${y + h * 0.08}, ${x + w * 0.92} ${y + h * 0.16}, ${x + w * 0.92} ${y + h * 0.34}
      C ${x + w * 0.92} ${y + h * 0.54}, ${x + w * 0.82} ${y + h * 0.7}, ${x + w * 0.5} ${y + h * 0.9}
      Z
    `;
    return (
      <g>
        <path d={path} fill="#eef2f7" stroke="#cbd5e1" strokeWidth="1.2" strokeDasharray="4 3" />
        <circle cx={cx} cy={cy} r={r} fill="#cbd5e1" />
        <polyline points={hill} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
  }

  if (maskShapeId === "circle" || maskShapeId === "oval") {
    return (
      <g>
        <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} fill="#eef2f7" stroke="#cbd5e1" strokeWidth="1.2" strokeDasharray="4 3" />
        <circle cx={cx} cy={cy} r={r} fill="#cbd5e1" />
        <polyline points={hill} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
  }

  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={rx} fill="#eef2f7" stroke="#cbd5e1" strokeWidth="1.2" strokeDasharray="4 3" />
      <circle cx={cx} cy={cy} r={r} fill="#cbd5e1" />
      <polyline points={hill} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

function TextBlockPreview({ x, y, w, h, role }: { x: number; y: number; w: number; h: number; role: string }) {
  const lines = role === "heading" ? 2 : Math.max(2, Math.min(5, Math.floor(h / 7)));
  const lineH = Math.min(6, Math.max(3, h / (lines * 1.5)));
  return (
    <g>
      {Array.from({ length: lines }).map((_, idx) => {
        const lineW = idx === lines - 1 ? w * 0.58 : w * (role === "heading" ? 0.78 : 0.9);
        return (
          <rect
            key={idx}
            x={x}
            y={y + idx * lineH * 1.8}
            width={Math.max(3, lineW)}
            height={lineH}
            rx={lineH / 2}
            fill={role === "heading" ? "#94a3b8" : "#cbd5e1"}
          />
        );
      })}
    </g>
  );
}

export default function TemplateThumbnail({
  blocks,
  formatGroup,
  className = "",
}: {
  blocks: NormBlock[];
  formatGroup: FormatGroup;
  className?: string;
}) {
  const page = FORMAT_PAGE_MM[formatGroup] || FORMAT_PAGE_MM.a4_portrait;
  const viewH = 120;
  const viewW = (page.w * 2 / page.h) * viewH;
  const margin = Math.max(7, Math.min(12, viewH * 0.08));
  const contentBox = { x: margin, y: margin, w: viewW - margin * 2, h: viewH - margin * 2 };
  const bleedBox = { x: 0, y: 0, w: viewW, h: viewH };

  return (
    <svg
      className={className}
      viewBox={`0 0 ${viewW} ${viewH}`}
      role="img"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
    >
      <rect x={0} y={0} width={viewW} height={viewH} rx={8} fill="#ffffff" />
      <rect x={viewW / 2 - 0.4} y={0} width={0.8} height={viewH} fill="#e2e8f0" />
      <rect x={margin} y={margin} width={contentBox.w} height={contentBox.h} fill="none" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />

      {blocks.map((block, idx) => {
        const base = block.type === "image" && block.bleed ? bleedBox : contentBox;
        const target = applyAnchor(base, block.anchor);
        const box = mapBox(block.box, target);

        if (block.type === "image") {
          return <ImageBlockPreview key={idx} x={box.x} y={box.y} w={box.w} h={box.h} maskShapeId={block.maskShapeId} />;
        }

        if (block.type === "text") {
          return <TextBlockPreview key={idx} x={box.x} y={box.y} w={box.w} h={box.h} role={block.role} />;
        }

        if (block.shape_id === "rectangle") {
          return (
            <rect
              key={idx}
              x={box.x}
              y={box.y}
              width={box.w}
              height={box.h}
              rx={Math.min(8, Math.min(box.w, box.h) * 0.08)}
              fill={block.fill_hex || "#dbeafe"}
              opacity={0.9}
            />
          );
        }

        if (block.shape_id === "circle" || block.shape_id === "oval") {
          return (
            <ellipse
              key={idx}
              cx={box.x + box.w / 2}
              cy={box.y + box.h / 2}
              rx={box.w / 2}
              ry={box.h / 2}
              fill={block.fill_hex || "#dbeafe"}
              opacity={0.9}
            />
          );
        }

        return (
          <polygon
            key={idx}
            points={shapePoints(block.shape_id, box.x, box.y, box.w, box.h)}
            fill={block.fill_hex || "#dbeafe"}
            opacity={0.9}
          />
        );
      })}
    </svg>
  );
}
